import { lazy, Suspense, useEffect, useRef, type ComponentType, type LazyExoticComponent } from 'react';
import { AdvisorPrepCard } from './components/AdvisorPrep';
import { Route, Switch, Redirect, useLocation } from 'wouter';
import './styles.css';

import { useAuth } from './state';
import { loadChunkWithRetry } from './loadChunk';
import { AppShell, PublicShell } from './AppShell';
import { ToastDeck, FullPageLoader } from './components';
import { FeedbackWidget } from './components/FeedbackWidget';
import { ConfirmHost } from './confirm';
import { useVisitorHeartbeat } from './visitorHeartbeat';
import { captureReferralFromUrl } from './referral';
import { ConsentBanner } from './consent/ConsentBanner';
import {
  installPixelLifecycle,
  trackPixelPageView,
  captureLiFatIdFromUrl,
  captureRdtCidFromUrl,
} from './consent/metaPixel';
// LinkedIn Insight Tag (client pixel) is DROPPED — its snap.licdn.com script
// triggered a deprecated Attribution Reporting API warning + a 429 from li_sync
// in real users' consoles, dragging Best Practices down, and LinkedIn offers no
// fix since it's their own script. Ad measurement continues server-side via the
// LinkedIn Conversions API (backend common/linkedinCapi.ts), fed by the
// li_fat_id we still capture below — that path is server-only, so no browser
// script, no console errors. The consent-gated loader lives on unused in
// ./consent/linkedinTag.ts; re-import installLinkedInLifecycle to bring it back.
import { installRedditPixelLifecycle, trackRedditPageVisit } from './consent/redditPixel';
import { installGoogleAdsLifecycle, trackGooglePageView } from './consent/googleAds';
import { isSecretUrlRoute } from './consent/consentManager';
import { installClarityLifecycle, updateClarityRoute } from './consent/clarity';
import { installConsentSyncLifecycle } from './consent/consentSync';
import { THIRD_PARTY_TRACKING_ENABLED } from './consent/consentManager';
import { PrerenderReadyBeacon } from './prerender';
import { useDocumentHead, refreshStructuredData } from './seo';
import { loadProductRating } from './productRating';

// ---------------------------------------------------------------------------
// Route-level code splitting.
//
// The home page is EAGER — it's the most-hit, LCP-critical page, so it ships in
// the entry chunk and paints without waiting for a second request. EVERY other
// route is lazily code-split below, so a first visit to "/" no longer downloads
// the admin console, the authenticated app, the A/B variants, or the sample
// pages. (This cut the entry bundle from ~3.4MB to a small fraction.)
//
// Prerendered public routes (/students, /plans, /learn, …) are lazy too — that's
// safe because <PrerenderReadyBeacon> lives inside the router's <Suspense> and
// only fires once a lazy route's real content has resolved (see the return JSX
// and src/prerender.ts).
//
// AuthModal is a shell-level component (opened from any nav/CTA), so it stays
// eager alongside the shells.
import { LandingPage } from './pages/Landing';
import { AuthModal } from './components/AuthModal';

/**
 * Adapt our named page exports to React.lazy's default-export contract, while
 * preserving each component's own prop types (so route props stay type-checked).
 * Multiple calls with the same import path resolve to one shared chunk.
 */
function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(
  loader: () => Promise<M>,
  name: K,
): LazyExoticComponent<Extract<M[K], ComponentType<any>>> {
  return lazy(() =>
    loadChunkWithRetry(loader).then((m) => ({ default: m[name] as ComponentType<any> })),
  ) as LazyExoticComponent<Extract<M[K], ComponentType<any>>>;
}

const VariantLandingPage = lazyNamed(() => import('./pages/VariantLanding'), 'VariantLandingPage');
const MethodologyPage = lazyNamed(() => import('./pages/Methodology'), 'MethodologyPage');
const PlansPage = lazyNamed(() => import('./pages/Plans'), 'PlansPage');
const TryPage = lazyNamed(() => import('./pages/Try'), 'TryPage');
const SparkPage = lazyNamed(() => import('./pages/Spark'), 'SparkPage');
const SparkStudioPage = lazyNamed(() => import('./pages/SparkStudio'), 'SparkStudioPage');
const SparkLibraryPage = lazyNamed(() => import('./pages/SparkLibrary'), 'SparkLibraryPage');
const SparkDemoPage = lazyNamed(() => import('./pages/SparkDemo'), 'SparkDemoPage');
const AuthPage = lazyNamed(() => import('./pages/Auth'), 'AuthPage');
const DashboardPage = lazyNamed(() => import('./pages/Dashboard'), 'DashboardPage');
const IdeasPage = lazyNamed(() => import('./pages/Ideas'), 'IdeasPage');
const NotesPage = lazyNamed(() => import('./pages/Notes'), 'NotesPage');
const NewIdeaPage = lazyNamed(() => import('./pages/IdeaForm'), 'NewIdeaPage');
const EditIdeaPage = lazyNamed(() => import('./pages/IdeaForm'), 'EditIdeaPage');
const IdeaCommandCenterPage = lazyNamed(() => import('./pages/IdeaCommandCenter'), 'IdeaCommandCenterPage');
const SessionRecordPage = lazyNamed(() => import('./pages/SessionRecordPage'), 'SessionRecordPage');
const IntelligenceCenterPage = lazyNamed(() => import('./pages/IntelligenceCenter'), 'IntelligenceCenterPage');
const SignalPreviewPage = lazyNamed(() => import('./pages/SignalPreview'), 'SignalPreviewPage');
const SignalsPage = lazyNamed(() => import('./pages/Signals'), 'SignalsPage');
const SignalsMonitoringPage = lazyNamed(() => import('./pages/SignalsMonitoring'), 'SignalsMonitoringPage');
const IdeaSessionPage = lazyNamed(() => import('./pages/Session'), 'IdeaSessionPage');
const MemoPage = lazyNamed(() => import('./pages/Memo'), 'MemoPage');
const NeedsDiscoverySessionPage = lazyNamed(() => import('./pages/NeedsDiscoverySession'), 'NeedsDiscoverySessionPage');
const InventionHelpPage = lazyNamed(() => import('./pages/professionals/InventionHelpPage'), 'InventionHelpPage');
const PrepareAttorneyPage = lazyNamed(() => import('./pages/professionals/PrepareAttorneyPage'), 'PrepareAttorneyPage');
const NeedsDiscoveryMemoPage = lazyNamed(() => import('./pages/NeedsDiscoveryMemo'), 'NeedsDiscoveryMemoPage');
const MarketModelPage = lazyNamed(() => import('./pages/MarketModel'), 'MarketModelPage');
const BusinessModelPage = lazyNamed(() => import('./pages/BusinessModel'), 'BusinessModelPage');
const BusinessCaseSessionPage = lazyNamed(() => import('./pages/BusinessCaseSession'), 'BusinessCaseSessionPage');
const BusinessCaseMemoPage = lazyNamed(() => import('./pages/BusinessCaseMemo'), 'BusinessCaseMemoPage');
const SearchStrategyPage = lazyNamed(() => import('./pages/SearchStrategy'), 'SearchStrategyPage');
const SearchReportPage = lazyNamed(() => import('./pages/SearchReport'), 'SearchReportPage');
const SearchStrategyLaunchpadPage = lazyNamed(() => import('./pages/SearchStrategyLaunchpad'), 'SearchStrategyLaunchpadPage');
const StrategyPage = lazyNamed(() => import('./pages/Strategy'), 'StrategyPage');
const LearnHomePage = lazyNamed(() => import('./pages/Education'), 'LearnHomePage');
const LearningPathPage = lazyNamed(() => import('./pages/Education'), 'LearningPathPage');
const LessonPage = lazyNamed(() => import('./pages/Education'), 'LessonPage');
const BadgesPage = lazyNamed(() => import('./pages/Badges'), 'BadgesPage');
const VocabularyPage = lazyNamed(() => import('./pages/Vocabulary'), 'VocabularyPage');
const StudyReviewPage = lazyNamed(() => import('./pages/StudyReview'), 'StudyReviewPage');
const GlossaryBrowsePage = lazyNamed(() => import('./pages/Glossary'), 'GlossaryBrowsePage');
const InventionLogPage = lazyNamed(() => import('./pages/InventionLog'), 'InventionLogPage');
const IdeaLogPage = lazyNamed(() => import('./pages/IdeaLog'), 'IdeaLogPage');
const PacketPage = lazyNamed(() => import('./pages/Packet'), 'PacketPage');
const LogIndexPage = lazyNamed(() => import('./pages/LogIndex'), 'LogIndexPage');
const LogPrintPage = lazyNamed(() => import('./pages/LogIndex'), 'LogPrintPage');
const LogBlankPage = lazyNamed(() => import('./pages/LogIndex'), 'LogBlankPage');
const RecordCheckPage = lazyNamed(() => import('./pages/RecordCheck'), 'RecordCheckPage');
const WitnessPage = lazyNamed(() => import('./pages/WitnessPage'), 'WitnessPage');
const MergeAccountsPage = lazyNamed(() => import('./pages/MergeAccountsPage'), 'MergeAccountsPage');
const PathwayPage = lazyNamed(() => import('./pages/pathway/PathwayPage'), 'PathwayPage');
const PathwayIndexPage = lazyNamed(() => import('./pages/pathway/PathwayIndex'), 'PathwayIndexPage');
const PathwayDemoPage = lazyNamed(() => import('./pages/pathway/PathwayDemo'), 'PathwayDemoPage');
const AdminPathwayPage = lazyNamed(() => import('./pages/pathway/AdminPathway'), 'AdminPathwayPage');
const AdminHardwareRulesPage = lazyNamed(() => import('./pages/hardware/AdminHardwareRules'), 'AdminHardwareRulesPage');
const DraftingPage = lazyNamed(() => import('./pages/drafting/DraftingPage'), 'DraftingPage');
const DraftingIndexPage = lazyNamed(() => import('./pages/drafting/DraftingIndex'), 'DraftingIndexPage');
const DraftingDemoPage = lazyNamed(() => import('./pages/drafting/DraftingDemo'), 'DraftingDemoPage');
const ManagerBoardPage = lazyNamed(() => import('./pages/manager/ManagerBoard'), 'ManagerBoardPage');
const ManagerInventionPage = lazyNamed(() => import('./pages/manager/ManagerInvention'), 'ManagerInventionPage');
const ManagerDemoPage = lazyNamed(() => import('./pages/manager/ManagerDemo'), 'ManagerDemoPage');
const AdminManagerPage = lazyNamed(() => import('./pages/manager/AdminManager'), 'AdminManagerPage');
const PrepPlanPage = lazyNamed(() => import('./pages/prepPlan/PrepPlanPage'), 'PrepPlanPage');
const DesignSketchIndexPage = lazyNamed(() => import('./pages/DesignSketch'), 'DesignSketchIndexPage');
const DrawingBriefPage = lazyNamed(() => import('./pages/DrawingBrief'), 'DrawingBriefPage');
const BusinessPlanningPage = lazyNamed(() => import('./pages/BusinessPlanning'), 'BusinessPlanningPage');
const BusinessPlanningIdeaPage = lazyNamed(() => import('./pages/BusinessPlanningIdea'), 'BusinessPlanningIdeaPage');
const CustomerDiscoveryPage = lazyNamed(() => import('./pages/CustomerDiscovery'), 'CustomerDiscoveryPage');
const BusinessCanvasPage = lazyNamed(() => import('./pages/BusinessCanvas'), 'BusinessCanvasPage');
const DesignWorkspacePage = lazyNamed(() => import('./pages/DesignWorkspace'), 'DesignWorkspacePage');
const AdminSurveysPage = lazyNamed(() => import('./pages/Admin'), 'AdminSurveysPage');
const AdminFieldsPage = lazyNamed(() => import('./pages/Admin'), 'AdminFieldsPage');
const AdminGuard = lazyNamed(() => import('./pages/Admin'), 'AdminGuard');
const AdminUsersPage = lazyNamed(() => import('./pages/AdminUsers'), 'AdminUsersPage');
const AdminUserDetailPage = lazyNamed(() => import('./pages/AdminUsers'), 'AdminUserDetailPage');
const AdminSurveyTemplatesPage = lazyNamed(() => import('./pages/AdminSurveyTemplates'), 'AdminSurveyTemplatesPage');
const AdminSurveyTemplateEditorPage = lazyNamed(() => import('./pages/AdminSurveyTemplates'), 'AdminSurveyTemplateEditorPage');
const AdminNewsletterPage = lazyNamed(() => import('./pages/AdminNewsletter'), 'AdminNewsletterPage');
const AdminLeadsPage = lazyNamed(() => import('./pages/AdminLeads'), 'AdminLeadsPage');
const AdminInfluencePage = lazyNamed(() => import('./pages/AdminInfluence'), 'AdminInfluencePage');
const TimesheetPage = lazyNamed(() => import('./pages/Timesheet'), 'TimesheetPage');
const BusinessDevelopmentMinePage = lazyNamed(() => import('./pages/BusinessDevelopmentMine'), 'BusinessDevelopmentMinePage');
const AdminConsultantsPage = lazyNamed(() => import('./pages/AdminConsultants'), 'AdminConsultantsPage');
const AdminFeedbackPage = lazyNamed(() => import('./pages/AdminFeedback'), 'AdminFeedbackPage');
const AdminMemoIssuesPage = lazyNamed(() => import('./pages/AdminMemoIssues'), 'AdminMemoIssuesPage');
const AdminPolicyNoticesPage = lazyNamed(() => import('./pages/AdminPolicyNotices'), 'AdminPolicyNoticesPage');
const AdminExampleLibraryPage = lazyNamed(() => import('./pages/AdminExampleLibrary'), 'AdminExampleLibraryPage');
const AdminSupportPage = lazyNamed(() => import('./pages/AdminSupport'), 'AdminSupportPage');
const AdminFlaggedPage = lazyNamed(() => import('./pages/AdminSupport'), 'AdminFlaggedPage');
const AdminContentPage = lazyNamed(() => import('./pages/AdminContent'), 'AdminContentPage');
const AdminKeywordsPage = lazyNamed(() => import('./pages/AdminKeywords'), 'AdminKeywordsPage');
const AdminSeoHealthPage = lazyNamed(() => import('./pages/AdminSeoHealth'), 'AdminSeoHealthPage');
const AdminRankTrackerPage = lazyNamed(() => import('./pages/AdminRankTracker'), 'AdminRankTrackerPage');
const AdminMonitorPage = lazyNamed(() => import('./pages/AdminMonitor'), 'AdminMonitorPage');
const AdminMonitorTimelinePage = lazyNamed(() => import('./pages/AdminMonitor'), 'AdminMonitorTimelinePage');
const AdminAffiliatesPage = lazyNamed(() => import('./pages/AdminAffiliates'), 'AdminAffiliatesPage');
const AdminAnnouncementsPage = lazyNamed(() => import('./pages/AdminAnnouncements'), 'AdminAnnouncementsPage');
const AdminCampaignsPage = lazyNamed(() => import('./pages/AdminCampaigns'), 'AdminCampaignsPage');
const AdminLifecyclePage = lazyNamed(() => import('./pages/AdminLifecycle'), 'AdminLifecyclePage');
const AdminRevenue = lazyNamed(() => import('./pages/AdminRevenue'), 'AdminRevenue');
const AdminCosts = lazyNamed(() => import('./pages/AdminCosts'), 'AdminCosts');
const AdminRdPage = lazyNamed(() => import('./pages/AdminRd'), 'AdminRdPage');
const AdminBusinessDevelopmentPage = lazyNamed(() => import('./pages/AdminBusinessDevelopment'), 'AdminBusinessDevelopmentPage');
const AdminCardSheetPage = lazyNamed(() => import('./pages/AdminCardSheet'), 'AdminCardSheetPage');
const AdminDashboardPage = lazyNamed(() => import('./pages/AdminDashboard'), 'AdminDashboardPage');
const UnsubscribePage = lazyNamed(() => import('./pages/Unsubscribe'), 'UnsubscribePage');
const AdminFamilyInquiriesPage = lazyNamed(() => import('./pages/AdminFamilyInquiries'), 'AdminFamilyInquiriesPage');
const AdminVisitorsPage = lazyNamed(() => import('./pages/AdminVisitors'), 'AdminVisitorsPage');
const ModulePreviewPage = lazyNamed(() => import('./pages/ModulePreview'), 'ModulePreviewPage');
const AboutPage = lazyNamed(() => import('./pages/Legal'), 'AboutPage');
const ContactPage = lazyNamed(() => import('./pages/Legal'), 'ContactPage');
const TermsPage = lazyNamed(() => import('./pages/Legal'), 'TermsPage');
const PrivacyPage = lazyNamed(() => import('./pages/Legal'), 'PrivacyPage');
const CookiesPage = lazyNamed(() => import('./pages/Legal'), 'CookiesPage');
const SecurityPage = lazyNamed(() => import('./pages/Legal'), 'SecurityPage');
const DemoMemoPage = lazyNamed(() => import('./pages/DemoMemo'), 'DemoMemoPage');
const SharedMemoPage = lazyNamed(() => import('./pages/SharedMemo'), 'SharedMemoPage');
const SharedPacketPage = lazyNamed(() => import('./pages/SharedPacket'), 'SharedPacketPage');
const GuestDemoPage = lazyNamed(() => import('./pages/GuestDemo'), 'GuestDemoPage');
const SampleSearchStrategyPage = lazyNamed(() => import('./pages/SampleSearchStrategy'), 'SampleSearchStrategyPage');
const SampleSearchReportPage = lazyNamed(() => import('./pages/SampleSearchReport'), 'SampleSearchReportPage');
const SampleNeedsDiscoveryPage = lazyNamed(() => import('./pages/SampleNeedsDiscovery'), 'SampleNeedsDiscoveryPage');
const SampleSparkPage = lazyNamed(() => import('./pages/SampleSpark'), 'SampleSparkPage');
const SampleCustomerDiscoveryPage = lazyNamed(() => import('./pages/SampleCustomerDiscovery'), 'SampleCustomerDiscoveryPage');
const SampleBusinessCasePage = lazyNamed(() => import('./pages/SampleBusinessCase'), 'SampleBusinessCasePage');
const CostComparisonPage = lazyNamed(() => import('./pages/cost/CostComparisonPage'), 'CostComparisonPage');
const DevelopInventionPage = lazyNamed(() => import('./pages/cost/DevelopInventionPage'), 'DevelopInventionPage');
const DevCostCalculatorPage = lazyNamed(() => import('./pages/cost/DevCostCalculatorPage'), 'DevCostCalculatorPage');
const HardwareCostCalculatorPage = lazyNamed(() => import('./pages/cost/HardwareCostCalculatorPage'), 'HardwareCostCalculatorPage');
const TamSamSomCalculatorPage = lazyNamed(() => import('./pages/cost/TamSamSomCalculatorPage'), 'TamSamSomCalculatorPage');
const SampleBusinessModelCanvasPage = lazyNamed(() => import('./pages/SampleBusinessModelCanvas'), 'SampleBusinessModelCanvasPage');
const SampleDesignPage = lazyNamed(() => import('./pages/SampleDesign'), 'SampleDesignPage');
const MarketplacePage = lazyNamed(() => import('./pages/Marketplace'), 'MarketplacePage');
const HowItWorksPage = lazyNamed(() => import('./pages/HowItWorks'), 'HowItWorksPage');
const StudentsPage = lazyNamed(() => import('./pages/Students'), 'StudentsPage');
const AffiliatesPage = lazyNamed(() => import('./pages/Affiliates'), 'AffiliatesPage');
const PatentCostCalculatorPage = lazyNamed(() => import('./pages/PatentCostCalculator'), 'PatentCostCalculatorPage');
// Invention drawings funnel: landing pages, free drawing-cost calculator, sample, quick start.
const DrawingsLanding = lazyNamed(() => import('./pages/drawings/DrawingsLanding'), 'DrawingsLanding');
const DrawingCostCalculatorPage = lazyNamed(() => import('./pages/drawings/DrawingCostCalculatorPage'), 'DrawingCostCalculatorPage');
const DrawingsSamplePage = lazyNamed(() => import('./pages/drawings/DrawingsSamplePage'), 'DrawingsSamplePage');
const DrawingsStartPage = lazyNamed(() => import('./pages/drawings/DrawingsStartPage'), 'DrawingsStartPage');
const DrawingsResumePage = lazyNamed(() => import('./pages/drawings/DrawingsStartPage'), 'DrawingsResumePage');
const AdvisorDemoPage = lazyNamed(() => import('./pages/demos/AdvisorDemoPage'), 'AdvisorDemoPage');
const DeepWorkDemoPage = lazyNamed(() => import('./pages/demos/DeepWorkDemoPage'), 'DeepWorkDemoPage');
const PriorArtDemoPage = lazyNamed(() => import('./pages/demos/PriorArtDemoPage'), 'PriorArtDemoPage');
const AdminDemosPage = lazyNamed(() => import('./pages/demos/AdminDemosPage'), 'AdminDemosPage');
const DesignSketchDemoPage = lazyNamed(() => import('./pages/drawings/DesignSketchDemoPage'), 'DesignSketchDemoPage');
const PatentDeadlineCalculatorPage = lazyNamed(() => import('./pages/PatentDeadlineCalculator'), 'PatentDeadlineCalculatorPage');
const PatentabilityCheckerPage = lazyNamed(() => import('./pages/PatentabilityChecker'), 'PatentabilityCheckerPage');
const PatentMaintenanceCalculatorPage = lazyNamed(() => import('./pages/PatentMaintenanceCalculator'), 'PatentMaintenanceCalculatorPage');
const InfographicPage = lazyNamed(() => import('./pages/Infographic'), 'InfographicPage');
const DynamicBlogIndex = lazyNamed(() => import('./pages/DynamicBlog'), 'DynamicBlogIndex');
const DynamicBlogPost = lazyNamed(() => import('./pages/DynamicBlog'), 'DynamicBlogPost');
const SettingsProfilePage = lazyNamed(() => import('./pages/Settings'), 'SettingsProfilePage');
const SettingsPreferencesPage = lazyNamed(() => import('./pages/Settings'), 'SettingsPreferencesPage');
const SettingsPrivacyPage = lazyNamed(() => import('./pages/Settings'), 'SettingsPrivacyPage');
const SettingsSubscriptionPage = lazyNamed(() => import('./pages/Settings'), 'SettingsSubscriptionPage');
const SettingsReferralPage = lazyNamed(() => import('./pages/Settings'), 'SettingsReferralPage');
const SettingsAccountPage = lazyNamed(() => import('./pages/Settings'), 'SettingsAccountPage');
const SettingsFeedbackPage = lazyNamed(() => import('./pages/Settings'), 'SettingsFeedbackPage');
const SettingsCommunityPage = lazyNamed(() => import('./pages/Settings'), 'SettingsCommunityPage');
const SettingsConnectionsPage = lazyNamed(() => import('./pages/SettingsConnections'), 'SettingsConnectionsPage');
const SettingsConnectionsCallbackPage = lazyNamed(() => import('./pages/SettingsConnections'), 'SettingsConnectionsCallbackPage');
const SettingsNotionCallbackPage = lazyNamed(() => import('./pages/SettingsConnections'), 'SettingsNotionCallbackPage');
const SettingsMicrosoftCallbackPage = lazyNamed(() => import('./pages/SettingsConnections'), 'SettingsMicrosoftCallbackPage');

export default function App() {
  const refresh = useAuth((s) => s.refresh);
  const ready = useAuth((s) => s.ready);
  const user = useAuth((s) => s.user);
  const [location] = useLocation();

  useEffect(() => {
    refresh();
    // Capture a `?ref=` referral code on first load so it survives the whole
    // sign-up journey and gets attached at profile completion.
    captureReferralFromUrl();
    // Stash LinkedIn's li_fat_id from the landing URL before it is navigated
    // away from — signup happens several routes later.
    captureLiFatIdFromUrl();
    // Same for Reddit's rdt_cid click id — captured at the landing URL so it
    // survives navigation to the conversion several routes later.
    captureRdtCidFromUrl();

    // Load the product-wide memo rating once, then re-emit the structured data so
    // the homepage's SoftwareApplication node can carry a real aggregateRating —
    // but only once there's a genuine, large-enough sample (see productRating.ts).
    // Public call, cheap, cached; a failure is silent (no aggregateRating emitted).
    loadProductRating().then(refreshStructuredData);

    // Install the marketing/analytics lifecycles LAZILY — on the visitor's
    // first interaction, or when they leave the page — NOT during load. Each
    // already gates on consent (nothing loads from Meta / Reddit / Google /
    // Clarity until granted). Keeping their ~350ms of script execution
    // out of the load window is what keeps them off Total Blocking Time: on a
    // throttled phone the main thread never has to run these before the page
    // becomes interactive. The scripts still load in time to matter:
    //   - an engaged visitor trips an interaction (scroll / tap / key / pointer)
    //     within moments, firing the initial PageView then;
    //   - a visitor who reads and bounces WITHOUT interacting is caught by the
    //     `visibilitychange -> hidden` leave signal, so their pageview still
    //     fires as they go (best-effort — an instant tab-close can beat the
    //     network send). This fires on the leave event, not during load, so it
    //     doesn't reintroduce load-time blocking.
    // Accepted trade-off: a page left open in the foreground and never touched
    // at all is not counted — an abandoned view we don't need. Per-route SPA
    // navigations are handled by the location effect below; on consent
    // withdrawal each lifecycle tears down.
    let teardowns: Array<() => void> = [];
    let started = false;
    // Genuine-gesture signals only. `mousemove` is deliberately EXCLUDED: it
    // fires on mere cursor presence — including ambiently during an automated
    // Lighthouse DESKTOP run — which loaded the third-party scripts inside the
    // load window and regressed desktop TBT + Best Practices. Every event below
    // requires a real tap, key, or scroll/wheel that a no-interaction load audit
    // never performs, so the scripts stay deferred in the audit on desktop too,
    // while real users still trip one within moments.
    const INTERACTION_EVENTS = [
      'pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel',
    ] as const;
    // Passive + capture so a scroll/touch listener can never delay the gesture.
    const triggerOpts = { passive: true, capture: true } as const;
    const onInteract = () => startThirdParty();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') startThirdParty();
    };
    const removeTriggers = () => {
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, onInteract, triggerOpts));
      document.removeEventListener('visibilitychange', onVisibility);
    };
    const startThirdParty = () => {
      if (!THIRD_PARTY_TRACKING_ENABLED) return; // all third-party tracking is off
      if (started) return; // idempotent — the first trigger wins
      started = true;
      removeTriggers();
      teardowns = [
        installPixelLifecycle(),
        installRedditPixelLifecycle(),
        installGoogleAdsLifecycle(),
        installClarityLifecycle(), // inert: CLARITY_ENABLED is false in consent/clarity.ts
        installConsentSyncLifecycle(),
      ];
    };
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, onInteract, triggerOpts));
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      removeTriggers();
      teardowns.forEach((t) => t());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire a Meta PageView on every in-app route change (SPA navigations don't
  // reload the page, so the pixel wouldn't otherwise count them). The very
  // first render is skipped here because the lifecycle above already fires the
  // initial PageView; this avoids double-counting the landing view. `trackPixel`
  // is a no-op whenever consent isn't granted, so this stays safe pre-consent.
  const firstNav = useRef(true);
  useEffect(() => {
    // Keep Clarity's route in sync on every navigation. This is what stops
    // recording the moment the user crosses from a public marketing page into
    // the authenticated app, before any confidential content renders. Runs on
    // the first render too (unlike the PageView below), so the landing route is
    // evaluated immediately.
    updateClarityRoute(location);

    if (firstNav.current) {
      firstNav.current = false;
      return;
    }
    // A secret-URL route (shared memo link) reached by an in-app navigation, after
    // ad tags already loaded on an earlier page: reload so the page starts fresh
    // with every tag off (the tracker gates refuse to load on these routes).
    if (isSecretUrlRoute(location)) {
      if (window.fbq || window.gtag || (window as { rdt?: unknown }).rdt || (window as { _linkedin_data_partner_ids?: unknown })._linkedin_data_partner_ids) {
        window.location.reload();
      }
      return;
    }
    trackPixelPageView();
    // Reddit PageVisit on the same SPA navigations, for parity with Meta. Also
    // a no-op until marketing consent is granted.
    trackRedditPageVisit();
    // Google Ads page_view on the same SPA navigations. Only sent once the visitor
    // has accepted and gtag.js has loaded (see consent/googleAds.ts).
    trackGooglePageView();
  }, [location]);

  // Time-on-site heartbeat. Pings every 30s while the tab is visible
  // so the admin's visitor view shows real cumulative session length
  // for every browser, not just the gap between first and last
  // tracked action. Installed at the App root so it covers every
  // route — anonymous /try, landing, dashboard, all of it.
  useVisitorHeartbeat();

  // Give each public page its own <title>, meta description, OG/Twitter text,
  // and canonical URL. Declared BEFORE the prerender signal so the head is set
  // in the same commit, before the readiness marker fires — the crawler snapshot
  // then carries the correct page-specific head. Unmapped routes fall back to
  // the brand default. See src/seo.ts.
  useDocumentHead(location);

  // Prerender readiness is now signaled by <PrerenderReadyBeacon>, rendered
  // inside the router's <Suspense> boundary below. That placement is deliberate:
  // with routes code-split, the beacon only commits once a (possibly lazy)
  // route's real content has resolved and painted — never while the loading
  // fallback shows — so the crawler snapshot captures the real page. No-op on
  // non-prerendered routes and inert for real users. See src/prerender.ts.

  // Scroll to top on every route change. Wouter doesn't do this on its
  // own, so navigating from the bottom of one page (e.g. a footer link)
  // leaves the new page scrolled to the same position the prior page
  // was. Without this, clicking "Cookie Policy" from the footer of a
  // long landing page renders the cookie policy already scrolled past
  // its own heading. `behavior: instant` (the default for window.scrollTo
  // when no behavior is set) avoids a smooth-scroll jank between pages.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo(0, 0);
    // On phones the body itself is the scroller, which window.scrollTo misses.
    document.body.scrollTop = 0;
    // Installed phone app (iOS standalone): after a route change from the menu
    // sheet, WebKit sometimes keeps painting the old screen until the member
    // drags the page. A one-frame compositing nudge forces the new page to paint.
    const standalone =
      window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
    if (!standalone) return;
    const root = document.getElementById('root');
    if (!root) return;
    const raf = requestAnimationFrame(() => {
      root.style.opacity = '0.999';
      requestAnimationFrame(() => {
        root.style.opacity = '';
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [location]);

  // Always show shell loading ONLY for authenticated routes that
  // depend on profile data. Public routes can render immediately.
  if (!ready) {
    const isPublic =
      location === '/' ||
      location === '/try' ||
      location === '/va' ||
      location === '/vb' ||
      location === '/vc' ||
      location === '/vd' ||
      location === '/ve' ||
      location === '/vf' ||
      location === '/vg' ||
      location === '/methodology' ||
      location.startsWith('/demo') ||
      location.startsWith('/signup') ||
      location.startsWith('/signin');
    if (!isPublic) return <FullPageLoader />;
  }

  return (
    <>
      <Suspense fallback={<FullPageLoader />}>
      <Switch>
        {/* Public routes */}
        {/* /landing always renders the public landing page regardless of
            auth state — gives signed-in users a way to revisit the marketing
            site (link from the footer wordmark and avatar menu). The
            page renders its own topbar and footer (including the
            newsletter signup band), so we don't wrap it in a shell. */}
        <Route path="/landing"><LandingPage /></Route>

        {/* /students — audience-specific landing page for high-school
            and college students plus their parents. Same brand, same
            domain, same data; just a focused funnel surface with the
            Family Bundle pricing front and center. CTAs carry
            `?audience=student` for tracking via /admin/visitors. */}
        <Route path="/students"><StudentsPage /></Route>
        <Route path="/affiliates"><AffiliatesPage /></Route>
        {/* /patent-cost-calculator — free, no-signup SEO tool (first of the
            free-tools wave). Public, prerendered, zero-AI. */}
        <Route path="/patent-cost-calculator"><PatentCostCalculatorPage /></Route>
        {/* /provisional-patent-deadline-calculator — free, no-signup SEO tool
            (second of the free-tools wave). Public, prerendered, zero-AI. */}
        <Route path="/provisional-patent-deadline-calculator"><PatentDeadlineCalculatorPage /></Route>
        {/* /patent-maintenance-fee-calculator — free, no-signup SEO tool (3rd of
            the free-tools wave): the 3.5/7.5/11.5-year maintenance schedule. */}
        <Route path="/patent-maintenance-fee-calculator"><PatentMaintenanceCalculatorPage /></Route>
        {/* /patentability-checker — free, no-signup AI tool (first of the AI
            converter wave): a first reading against the four patent tests. */}
        <Route path="/patentability-checker"><PatentabilityCheckerPage /></Route>
        {/* Invention drawings funnel (Design Sketch sold on its own). Public and
            prerendered, except the per-visitor quick start (noindex). */}
        <Route path="/invention-concept-drawings"><DrawingsLanding variant="pillar" /></Route>
        <Route path="/invention-3d-model"><DrawingsLanding variant="3d" /></Route>
        <Route path="/patent-drawing-cost-calculator"><DrawingCostCalculatorPage /></Route>
        {/* /cost-to-develop-an-invention — public guide comparing four 0-to-1 paths, sourced. */}
        {/* Pillar: the invention-development topic cluster hub. */}
        <Route path="/how-to-develop-an-invention"><DevelopInventionPage /></Route>
        <Route path="/cost-to-develop-an-invention"><CostComparisonPage /></Route>
        {/* Working with professionals: the hub and the attorney audience page (voice.md positioning). */}
        <Route path="/invention-help"><InventionHelpPage /></Route>
        <Route path="/prepare-for-patent-attorney"><PrepareAttorneyPage /></Route>
        {/* Free calculator: the guide's figures with your own numbers. */}
        <Route path="/invention-development-cost-calculator"><DevCostCalculatorPage /></Route>
        {/* Free calculator: cash needed before the first sale of a physical product. */}
        <Route path="/hardware-development-cost-calculator"><HardwareCostCalculatorPage /></Route>
        {/* First finance free tool: TAM/SAM/SOM, on the live market model's own engine. */}
        <Route path="/tam-sam-som-calculator"><TamSamSomCalculatorPage /></Route>
        <Route path="/invention-concept-drawings/sample"><DrawingsSamplePage /></Route>
        <Route path="/invention-concept-drawings/resume/:token">{(params) => <DrawingsResumePage token={params.token} />}</Route>
        <Route path="/invention-concept-drawings/start/:draftId">{(params) => <DrawingsStartPage draftId={params.draftId} />}</Route>
        {/* /infographic — public download page for the spark-to-business
            one-pager (general inventor/entrepreneur audience). */}
        <Route path="/infographic"><ShellRoute><InfographicPage /></ShellRoute></Route>

        <Route path="/">
          {user ? <Redirect to="/dashboard" /> : <LandingPage />}
        </Route>
        {/* /v2 — the light, minimalist A/B variant of the homepage. Same page,
            same content, same funnel; only the visual skin differs (light prop).
            Signed-in visitors redirect to the dashboard like the control. Drive
            ad traffic here to split-test against /. */}
        <Route path="/v2">
          {user ? <Redirect to="/dashboard" /> : <LandingPage light />}
        </Route>
        {/* /va /vb /vc /vd — four unlisted design-direction A/B variants
            (Calm / Proof / Editorial / App). Same funnel as the control:
            real sign-up modal + real Stripe checkout, only the visual skin
            differs. Signed-in visitors redirect to the dashboard like the
            control. Drive test traffic here to compare against /. */}
        <Route path="/va">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="a" />}
        </Route>
        <Route path="/vb">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="b" />}
        </Route>
        <Route path="/vc">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="c" />}
        </Route>
        <Route path="/vd">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="d" />}
        </Route>
        <Route path="/ve">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="e" />}
        </Route>
        {/* Public methodology / transparency page. */}
        <Route path="/methodology" component={MethodologyPage} />
        <Route path="/plans" component={PlansPage} />
        <Route path="/vf">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="f" />}
        </Route>
        <Route path="/vg">
          {user ? <Redirect to="/dashboard" /> : <VariantLandingPage variant="g" />}
        </Route>
        <Route path="/try" component={TryPage} />
        {/* /spark is auth-aware: a logged-in member gets the in-app Spark
            studio (metered ideation tool) inside the app shell; everyone else
            gets the anonymous marketing front door. Gated on `ready` so a
            member never flashes the marketing page before auth resolves. */}
        {/* Spark Library — member-only full history of every Spark run. Placed
            before /spark; wouter matches exact paths so this never shadows the
            studio, but keeping it first is defensive. */}
        <Route path="/spark/library">
          <SparkLibraryRoute />
        </Route>
        {/* Spark guided demo: public, read only, hideable in Admin → Guided demos. */}
        <Route path="/spark/demo">
          {user ? (
            <AppShell>
              <SparkDemoPage />
            </AppShell>
          ) : (
            <PublicShell>
              <SparkDemoPage />
            </PublicShell>
          )}
        </Route>
        <Route path="/spark">
          <SparkRoute />
        </Route>
        <Route path="/signup" component={AuthPage} />
        <Route path="/signin" component={AuthPage} />

        {/* Legal & informational pages — accessible from the footer at all
            times. Use the shell that matches the user's auth state so the
            chrome doesn't shift when a logged-in user clicks Terms. */}
        <Route path="/about">
          <ShellRoute><AboutPage /></ShellRoute>
        </Route>
        <Route path="/contact">
          <ShellRoute><ContactPage /></ShellRoute>
        </Route>
        <Route path="/terms">
          <ShellRoute><TermsPage /></ShellRoute>
        </Route>
        <Route path="/privacy">
          <ShellRoute><PrivacyPage /></ShellRoute>
        </Route>
        <Route path="/security">
          <ShellRoute><SecurityPage /></ShellRoute>
        </Route>
        {/* Public demo memo — anyone can read the seeded sample memo
            without signing in. The DemoMemoPage manages its own minimal
            topbar so it doesn't carry the full PublicShell chrome that
            would compete with the strong sign-up CTAs already on the
            page. */}
        {/* Shared memo link (Secure Memo Sharing): public, read-only, no app chrome. */}
        {/* Public record check for printed Invention Logs: no sign-in, no app chrome. */}
        {/* Public witness page: a trusted person reads and countersigns log entries (no sign-in). */}
        {/* Public merge page from the merge-offer email: confirm with a text code, then two accounts become one. */}
        <Route path="/merge-accounts/:token">
          {(params) => <MergeAccountsPage token={params.token} />}
        </Route>
        <Route path="/witness/:token">
          {(params) => <WitnessPage token={params.token} />}
        </Route>
        <Route path="/check/:docId">
          {(params) => <RecordCheckPage docId={params.docId} />}
        </Route>
        <Route path="/check">
          <RecordCheckPage docId="" />
        </Route>
        {/* Before the memo route, or ":documentId" would match "packet". */}
        <Route path="/shared/packet/:token">
          {(params) => <SharedPacketPage token={params.token} />}
        </Route>
        <Route path="/shared/:documentId/:token">
          {(params) => <SharedMemoPage documentId={params.documentId} token={params.token} />}
        </Route>
        <Route path="/demo/memo">
          <DemoMemoPage />
        </Route>
        {/* Try It Live — the guest demo: run a real, live advisor session on a
            curated sample idea, then see the ungated verdict. Standalone page
            with its own minimal top bar (no PublicShell chrome). */}
        <Route path="/demo/live">
          <GuestDemoPage />
        </Route>
        <Route path="/demo/sample-search-strategy">
          <SampleSearchStrategyPage />
        </Route>
        <Route path="/demo/sample-search-report">
          <SampleSearchReportPage />
        </Route>
        <Route path="/demo/sample-needs-discovery">
          <SampleNeedsDiscoveryPage />
        </Route>
        <Route path="/demo/sample-spark">
          <SampleSparkPage />
        </Route>
        <Route path="/demo/sample-customer-discovery">
          <SampleCustomerDiscoveryPage />
        </Route>
        <Route path="/demo/sample-business-case">
          <SampleBusinessCasePage />
        </Route>
        <Route path="/demo/sample-business-model-canvas">
          <SampleBusinessModelCanvasPage />
        </Route>
        <Route path="/demo/sample-design">
          <SampleDesignPage />
        </Route>
        <Route path="/marketplace">
          {user ? (
            <AppShell>
              <MarketplacePage />
            </AppShell>
          ) : (
            <PublicShell>
              <MarketplacePage />
            </PublicShell>
          )}
        </Route>
        <Route path="/how-it-works">
          <ShellRoute><HowItWorksPage /></ShellRoute>
        </Route>
        <Route path="/cookies">
          <ShellRoute><CookiesPage /></ShellRoute>
        </Route>
        {/* Public one-click unsubscribe landing (campaign email footers).
            Self-contained full-screen page; no shell, no auth. */}
        <Route path="/unsubscribe" component={UnsubscribePage} />
        {/* The blog is pre-rendered static HTML at .html URLs (see
            blog/plugin.mjs), which stays the SEO source of truth. These clean
            routes serve the fast static page for known posts, and render a
            brand-new (not-yet-built) post live from the database so it appears
            immediately after the weekly engine publishes it. See DynamicBlog. */}
        <Route path="/blog" component={DynamicBlogIndex} />
        <Route path="/blog/:slug">
          {(params) => <DynamicBlogPost slug={params.slug} />}
        </Route>

        {/* Authenticated routes */}
        <Route path="/dashboard">
          <Authenticated>
            {/* A consultant-only account (invited to log time, not an inventor
                or admin) lands on their timesheet instead of the product. */}
            {user && (user.roles ?? []).includes('consultant') && !(user.roles ?? []).includes('admin')
              ? <Redirect to="/timesheet" />
              : user && (user.roles ?? []).includes('bd') && !(user.roles ?? []).includes('admin') && !(user.roles ?? []).includes('consultant')
                ? <Redirect to="/bd" />
                : <DashboardPage />}
          </Authenticated>
        </Route>
        <Route path="/intelligence">
          <Authenticated>
            <IntelligenceCenterPage />
          </Authenticated>
        </Route>
        <Route path="/signals">
          <Authenticated>
            <SignalsMonitoringPage />
          </Authenticated>
        </Route>
        <Route path="/intelligence/signals/:slug">
          {(params) => (
            <Authenticated>
              <SignalPreviewPage slug={params.slug} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas">
          <Authenticated>
            <IdeasPage />
          </Authenticated>
        </Route>
        <Route path="/ideas/new">
          <Authenticated>
            <NewIdeaPage />
          </Authenticated>
        </Route>
        <Route path="/ideas/:id/edit">
          {(params) => (
            <Authenticated>
              <EditIdeaPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/signals">
          {(params) => (
            <Authenticated>
              <SignalsPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/session">
          {(params) => (
            <Authenticated boundMain>
              <IdeaSessionPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        {/* Session record: an advisor conversation read as a document (separate from the chat and the memo). */}
        <Route path="/ideas/:id/session-record/:sessionId">
          {(params) => (
            <Authenticated>
              <SessionRecordPage ideaId={params.id} sessionId={params.sessionId} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/memos/:version">
          {(params) => (
            <Authenticated>
              <MemoPage ideaId={params.id} version={params.version} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/needs-discovery">
          {(params) => (
            <Authenticated boundMain>
              <NeedsDiscoverySessionPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/needs-discovery-memo/:version">
          {(params) => (
            <Authenticated>
              <NeedsDiscoveryMemoPage
                ideaId={params.id}
                version={params.version}
              />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/market-model">
          {(params) => (
            <Authenticated>
              <MarketModelPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/business-model">
          {(params) => (
            <Authenticated>
              <BusinessModelPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/business-case">
          {(params) => (
            <Authenticated boundMain>
              <BusinessCaseSessionPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/business-case-memo/:version">
          {(params) => (
            <Authenticated>
              <BusinessCaseMemoPage
                ideaId={params.id}
                version={params.version}
              />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/business-planning">
          {(params) => (
            <Authenticated>
              <BusinessPlanningIdeaPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        {/* Notes: quick notes + memo highlights, for one idea. */}
        <Route path="/ideas/:id/notes">
          {(params) => (
            <Authenticated>
              <NotesPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/progress">
          {(params) => (
            <Authenticated>
              <BusinessPlanningIdeaPage ideaId={params.id} focus="tracker" />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/customer-discovery">
          {(params) => (
            <Authenticated>
              <CustomerDiscoveryPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/business-canvas">
          {(params) => (
            <Authenticated>
              <BusinessCanvasPage ideaId={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/search-strategy">
          <Authenticated>
            <SearchStrategyLaunchpadPage />
          </Authenticated>
        </Route>
        <Route path="/ideas/:id/search-strategy">
          {(params) => (
            <Authenticated>
              <SearchStrategyPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/search-report">
          {(params) => (
            <Authenticated>
              <SearchReportPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        {/* My Invention Log — design decomposition workspace for one idea. */}
        <Route path="/ideas/:id/sketch">
          {(params) => (
            <Authenticated>
              <DrawingBriefPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/log/print">
          {(params) => (
            <Authenticated>
              <LogPrintPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/packet">
          {(params) => (
            <Authenticated>
              <PacketPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id/log">
          {(params) => (
            <Authenticated>
              <IdeaLogPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        {/* Pathway Navigator: filing strategy for one idea (bought per invention). */}
        <Route path="/ideas/:id/pathway">
          {(params) => (
            <Authenticated>
              <PathwayPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        {/* Drafting Workbench (Prepare My Filing): bought per invention. */}
        <Route path="/ideas/:id/drafting">
          {(params) => (
            <Authenticated boundMain>
              <DraftingPage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/drafting/demo">
          {user ? (
            <AppShell>
              <DraftingDemoPage />
            </AppShell>
          ) : (
            <PublicShell>
              <DraftingDemoPage />
            </PublicShell>
          )}
        </Route>
        <Route path="/drafting">
          <Authenticated>
            <DraftingIndexPage />
          </Authenticated>
        </Route>
        {/* Cost & Filing Manager: fees, deadlines and filing status per invention. */}
        {/* Cost & Filing Manager guided demo: public, read only, hideable in Admin → Guided demos. */}
        <Route path="/manager/demo/:ideaId?">
          {(params) =>
            user ? (
              <AppShell boundMain boundOn="desktop">
                <ManagerDemoPage ideaId={params.ideaId} />
              </AppShell>
            ) : (
              <PublicShell>
                <ManagerDemoPage ideaId={params.ideaId} />
              </PublicShell>
            )
          }
        </Route>
        <Route path="/manager/:ideaId">
          {(params) => (
            <Authenticated boundMain boundOn="desktop">
              <ManagerInventionPage id={params.ideaId} />
            </Authenticated>
          )}
        </Route>
        <Route path="/manager">
          <Authenticated boundMain boundOn="desktop">
            <ManagerBoardPage />
          </Authenticated>
        </Route>
        {/* Provisional Patent Prep Plan: choose the invention, then Stripe. */}
        <Route path="/prep-plan">
          <Authenticated>
            <PrepPlanPage />
          </Authenticated>
        </Route>
        <Route path="/pathway/demo">
          {user ? (
            <AppShell>
              <PathwayDemoPage />
            </AppShell>
          ) : (
            <PublicShell>
              <PathwayDemoPage />
            </PublicShell>
          )}
        </Route>
        <Route path="/pathway">
          <Authenticated>
            <PathwayIndexPage />
          </Authenticated>
        </Route>
        <Route path="/log/blank">
          <LogBlankPage />
        </Route>
        <Route path="/log">
          <Authenticated>
            <LogIndexPage />
          </Authenticated>
        </Route>
        <Route path="/ideas/:id/design">
          {(params) => (
            <Authenticated>
              <DesignWorkspacePage id={params.id} />
            </Authenticated>
          )}
        </Route>
        <Route path="/ideas/:id">
          {(params) => (
            <Authenticated>
              <IdeaCommandCenterPage id={params.id} />
            </Authenticated>
          )}
        </Route>

        {/* The Notes inbox: every quick note and memo highlight. */}
        <Route path="/notes">
          <Authenticated>
            <NotesPage />
          </Authenticated>
        </Route>
        <Route path="/strategy">
          <Authenticated>
            <StrategyPage />
          </Authenticated>
        </Route>
        {/* The former "coming soon" advisor previews now point at the Build Lab preview. */}
        <Route path="/advisors/:slug">
          <Redirect to="/modules/digital-build-lab" />
        </Route>

        {/* My Invention Log index — lists ideas; pick one to open its
            design decomposition workspace. */}
        {/* Design Sketch index: lists ideas with where each drawing brief stands. */}
        {/* Read-only Design Sketch demo (the Clip-On Tent Lantern), in every member's account. */}
        {/* Guided feature demos (read-only walkthroughs on the lantern). */}
        <Route path="/demos/:kind">
          {(params) =>
            ['patent', 'needs_discovery', 'business_case'].includes(params.kind) ? (
              <Authenticated>
                <AdvisorDemoPage kind={params.kind as 'patent'} />
              </Authenticated>
            ) : params.kind === 'prior_art' ? (
              <Authenticated>
                <PriorArtDemoPage />
              </Authenticated>
            ) : ['design', 'customer_discovery', 'business_canvas', 'progress', 'command_center', 'signals', 'invention_log'].includes(params.kind) ? (
              <Authenticated>
                <DeepWorkDemoPage kind={params.kind as 'design'} />
              </Authenticated>
            ) : (
              <Redirect to="/strategy" />
            )
          }
        </Route>
        <Route path="/admin/demos">
          <Authenticated>
            <AdminGuard>
              <AdminDemosPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/design-sketch/demo">
          <Authenticated>
            <DesignSketchDemoPage />
          </Authenticated>
        </Route>
        <Route path="/design-sketch">
          <Authenticated>
            <DesignSketchIndexPage />
          </Authenticated>
        </Route>

        <Route path="/invention-log">
          <Authenticated>
            <InventionLogPage />
          </Authenticated>
        </Route>

        {/* My Business Planning hub — lists ideas with their per-feature
            readiness; pick one to open its per-idea business-planning page. */}
        <Route path="/business-planning">
          <Authenticated>
            <BusinessPlanningPage />
          </Authenticated>
        </Route>

        {/* /learn is public — anonymous testers can browse the
            Education Center catalog (curriculums, paths, lesson
            titles) and read free-preview lessons before signing up.
            Lesson bodies that aren't marked isFreePreview return a
            locked stub for non-subscribers (including anonymous
            visitors), same as a logged-in free-tier user. */}
        <Route path="/learn">
          {user ? (
            <AppShell>
              <LearnHomePage />
            </AppShell>
          ) : (
            <PublicShell>
              <LearnHomePage />
            </PublicShell>
          )}
        </Route>
        <Route path="/learn/paths/:slug">
          {(params) =>
            user ? (
              <AppShell>
                <LearningPathPage slug={params.slug} />
              </AppShell>
            ) : (
              <PublicShell>
                <LearningPathPage slug={params.slug} />
              </PublicShell>
            )
          }
        </Route>
        <Route path="/learn/lessons/:slug">
          {(params) =>
            user ? (
              <AppShell>
                <LessonPage slug={params.slug} />
              </AppShell>
            ) : (
              <PublicShell>
                <LessonPage slug={params.slug} />
              </PublicShell>
            )
          }
        </Route>

        {/* EntreDash Glossary — public browsable reference of every
            defined term. Free like hover definitions and the lesson
            catalog, so anonymous visitors can browse it too. Saving a
            term is the subscriber perk, gated within the page. */}
        <Route path="/learn/glossary">
          {user ? (
            <AppShell>
              <GlossaryBrowsePage />
            </AppShell>
          ) : (
            <PublicShell>
              <GlossaryBrowsePage />
            </PublicShell>
          )}
        </Route>

        {/* Invention Vocabulary — saved terms + spaced-repetition study.
            Subscriber features (the methods enforce it); both pages also
            show a graceful gate. Require auth like the rest of /learn's
            personal surfaces. */}
        <Route path="/learn/vocabulary/study">
          <Authenticated>
            <StudyReviewPage />
          </Authenticated>
        </Route>
        <Route path="/learn/vocabulary">
          <Authenticated>
            <VocabularyPage />
          </Authenticated>
        </Route>

        {/* Badges & Rewards: private, personal achievement and progress
            view. Replaces the old peer leaderboard so an inventor's
            confidential study habits stay visible only to them. */}
        <Route path="/badges">
          <Authenticated>
            <BadgesPage />
          </Authenticated>
        </Route>

        {/* Legacy /leaderboard URL — keep an in-app redirect so any
            stale bookmarks land on the renamed page. */}
        <Route path="/leaderboard">
          <Redirect to="/badges" />
        </Route>

        {/* Admin portal — read-only, gated by the `admin` role both
            client-side (AdminGuard) and on every method handler. The
            client guard avoids a flash of forbidden chrome; the
            server check is the actual security boundary. */}
        <Route path="/admin">
          <Authenticated>
            <AdminGuard>
              <AdminDashboardPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/manager">
          <Authenticated>
            <AdminGuard>
              <AdminManagerPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/pathway">
          <Authenticated>
            <AdminGuard>
              <AdminPathwayPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/hardware-rules">
          <Authenticated>
            <AdminGuard>
              <AdminHardwareRulesPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/users">
          <Authenticated>
            <AdminGuard>
              <AdminUsersPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/users/:id">
          {(params) => (
            <Authenticated>
              <AdminGuard>
                <AdminUserDetailPage userId={params.id} />
              </AdminGuard>
            </Authenticated>
          )}
        </Route>
        <Route path="/admin/support">
          <Authenticated>
            <AdminGuard>
              <AdminSupportPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/memo-issues">
          <Authenticated>
            <AdminGuard>
              <AdminMemoIssuesPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/policy-notices">
          <Authenticated>
            <AdminGuard>
              <AdminPolicyNoticesPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/examples">
          <Authenticated>
            <AdminGuard>
              <AdminExampleLibraryPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/flagged">
          <Authenticated>
            <AdminGuard>
              <AdminFlaggedPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/surveys">
          <Authenticated>
            <AdminGuard>
              <AdminSurveysPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        {/* Survey template list and editor. The editor takes a slug:
            `new` creates a fresh draft, an existing slug edits that
            template in place. */}
        <Route path="/admin/survey-templates">
          <Authenticated>
            <AdminGuard>
              <AdminSurveyTemplatesPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/survey-templates/:slug">
          {(params) => (
            <Authenticated>
              <AdminGuard>
                <AdminSurveyTemplateEditorPage
                  slug={decodeURIComponent(params.slug)}
                />
              </AdminGuard>
            </Authenticated>
          )}
        </Route>
        <Route path="/admin/fields">
          <Authenticated>
            <AdminGuard>
              <AdminFieldsPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/newsletter">
          <Authenticated>
            <AdminGuard>
              <AdminNewsletterPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/leads">
          <Authenticated>
            <AdminGuard>
              <AdminLeadsPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/influencer">
          <Authenticated>
            <AdminGuard>
              <AdminInfluencePage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/consultants">
          <Authenticated>
            <AdminGuard>
              <AdminConsultantsPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        {/* Consultant timesheet — the page self-gates by role (consultant sees
            their own; admin is bounced to the console; anyone else sees an
            empty state). */}
        <Route path="/timesheet">
          <Authenticated>
            <TimesheetPage />
          </Authenticated>
        </Route>
        {/* Business development teammate board — a standalone, scoped surface
            with no inventor app shell. Self-gates: a signed-in user with no
            portal access sees a friendly note rather than anyone's data. */}
        <Route path="/bd">
          <BdMineRoute />
        </Route>
        {/* Old path kept as a redirect so existing bookmarks still land here. */}
        <Route path="/admin/influence">
          <Redirect to="/admin/influencer" />
        </Route>
        <Route path="/admin/feedback">
          <Authenticated>
            <AdminGuard>
              <AdminFeedbackPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/family-inquiries">
          <Authenticated>
            <AdminGuard>
              <AdminFamilyInquiriesPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/visitors">
          <Authenticated>
            <AdminGuard>
              <AdminVisitorsPage />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/content">
          <Authenticated>
            <AdminGuard>
              <AdminContentPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/keywords">
          <Authenticated>
            <AdminGuard>
              <AdminKeywordsPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/seo-health">
          <Authenticated>
            <AdminGuard>
              <AdminSeoHealthPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/rank-tracker">
          <Authenticated>
            <AdminGuard>
              <AdminRankTrackerPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/monitor/e/:entityId">
          {(params) => (
            <Authenticated>
              <AdminGuard>
                <AdminMonitorTimelinePage entityId={params.entityId} />
              </AdminGuard>
            </Authenticated>
          )}
        </Route>

        <Route path="/admin/monitor">
          <Authenticated>
            <AdminGuard>
              <AdminMonitorPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/affiliates">
          <Authenticated>
            <AdminGuard>
              <AdminAffiliatesPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/announcements">
          <Authenticated>
            <AdminGuard>
              <AdminAnnouncementsPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/campaigns">
          <Authenticated>
            <AdminGuard>
              <AdminCampaignsPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/lifecycle">
          <Authenticated>
            <AdminGuard>
              <AdminLifecyclePage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/costs">
          <Authenticated>
            <AdminGuard>
              <AdminCosts />
            </AdminGuard>
          </Authenticated>
        </Route>
        <Route path="/admin/rd">
          <Authenticated>
            <AdminGuard>
              <AdminRdPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/business-development">
          <Authenticated>
            <AdminGuard>
              <AdminBusinessDevelopmentPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        {/* Export sheet for the eight hero cards, for promo stills. Admin-gated
            because it is a production tool, not a customer surface. */}
        <Route path="/admin/card-sheet">
          <Authenticated>
            <AdminGuard>
              <AdminCardSheetPage />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/admin/revenue">
          <Authenticated>
            <AdminGuard>
              <AdminRevenue />
            </AdminGuard>
          </Authenticated>
        </Route>

        <Route path="/modules/:slug">
          {(params) => (
            <Authenticated>
              <ModulePreviewPage slug={params.slug} />
            </Authenticated>
          )}
        </Route>

        <Route path="/settings">
          <Authenticated>
            <SettingsProfilePage />
          </Authenticated>
        </Route>
        <Route path="/settings/preferences">
          <Authenticated>
            <SettingsPreferencesPage />
          </Authenticated>
        </Route>
        <Route path="/settings/privacy">
          <Authenticated>
            <SettingsPrivacyPage />
          </Authenticated>
        </Route>
        <Route path="/settings/subscription">
          <Authenticated>
            <SettingsSubscriptionPage />
          </Authenticated>
        </Route>
        <Route path="/settings/referrals">
          <Authenticated>
            <SettingsReferralPage />
          </Authenticated>
        </Route>
        <Route path="/settings/feedback">
          <Authenticated>
            <SettingsFeedbackPage />
          </Authenticated>
        </Route>
        <Route path="/settings/community">
          <Authenticated>
            <SettingsCommunityPage />
          </Authenticated>
        </Route>
        {/* Google OAuth redirect target — finishes the connection, then returns
            to the Connected apps panel. Listed before the panel route for clarity;
            wouter matches exact paths so order isn't load-bearing. */}
        {/* Notion OAuth redirect target. */}
        {/* Microsoft (OneDrive) OAuth redirect target. */}
        <Route path="/settings/connections/microsoft/callback">
          <Authenticated>
            <SettingsMicrosoftCallbackPage />
          </Authenticated>
        </Route>
        <Route path="/settings/connections/notion/callback">
          <Authenticated>
            <SettingsNotionCallbackPage />
          </Authenticated>
        </Route>
        <Route path="/settings/connections/callback">
          <Authenticated>
            <SettingsConnectionsCallbackPage />
          </Authenticated>
        </Route>
        <Route path="/settings/connections">
          <Authenticated>
            <SettingsConnectionsPage />
          </Authenticated>
        </Route>
        <Route path="/settings/account">
          <Authenticated>
            <SettingsAccountPage />
          </Authenticated>
        </Route>

        <Route>
          <Redirect to={user ? '/dashboard' : '/'} />
        </Route>
      </Switch>
      {/* Inside <Suspense>: fires the prerender readiness marker only after a
          (possibly lazy) route's real content has resolved and painted. */}
      <PrerenderReadyBeacon pathname={location} authReady={ready} />
      </Suspense>
      <ToastDeck />
      {/* "Your advisor is getting ready" progress while a session opens. */}
      <AdvisorPrepCard />
      <ConfirmHost />
      {/* Feedback FAB on every page, /spark included — cold visitors are exactly
          the audience whose reactions are most worth hearing, and the widget
          already supports anonymous submissions. It anchors to the bottom-left
          gutter (clear of the hero input) and floats over the now-scrolling
          /spark page like any other. */}
      <FeedbackWidget />
      {/* Global sign in / sign up popup — opened from any nav/CTA trigger via
          useAuthModal, overlays whatever page is showing. */}
      <AuthModal />
      {/* Cookie consent surface — banner (EU/UK), opt-out note (elsewhere), or
          the preferences modal (footer link). Governs whether the Meta Pixel
          is allowed to load. Mounted last so it overlays page chrome. */}
      <ConsentBanner />
    </>
  );
}

function Authenticated({
  children,
  // Forwarded to AppShell. Session pages set this so <main> stays viewport-
  // bounded and their internal chat scroll works; every other page leaves it
  // false so <main> grows to content and the footer follows.
  boundMain,
  boundOn,
}: {
  children: React.ReactNode;
  boundMain?: boolean;
  boundOn?: 'all' | 'desktop';
}) {
  const user = useAuth((s) => s.user);
  const ready = useAuth((s) => s.ready);
  const [location] = useLocation();

  if (!ready) return <FullPageLoader />;
  if (!user) {
    return <Redirect to={`/signin?next=${encodeURIComponent(location)}`} />;
  }
  return <AppShell boundMain={boundMain} boundOn={boundOn}>{children}</AppShell>;
}

/**
 * /spark router. Signed-in members get the in-app Spark studio inside the app
 * shell; everyone else gets the anonymous marketing front door. Gated on
 * `ready` so a returning member doesn't flash the marketing page before their
 * session resolves.
 */
function SparkRoute() {
  const user = useAuth((s) => s.user);
  const ready = useAuth((s) => s.ready);
  if (!ready) return <FullPageLoader />;
  if (user) {
    return (
      <AppShell>
        <SparkStudioPage />
      </AppShell>
    );
  }
  return <SparkPage />;
}

/**
 * /spark/library — the full Spark history. Member-only: an anonymous visitor is
 * sent to the Spark front door rather than shown an empty, meaningless library.
 */
function SparkLibraryRoute() {
  const user = useAuth((s) => s.user);
  const ready = useAuth((s) => s.ready);
  if (!ready) return <FullPageLoader />;
  if (!user) return <Redirect to="/spark" />;
  return (
    <AppShell>
      <SparkLibraryPage />
    </AppShell>
  );
}

/**
 * Business development teammate board. Requires a signed-in user, but renders
 * standalone (no inventor AppShell) so a teammate never sees the inventor
 * product — the page itself resolves their access and shows their own board or
 * a friendly no-access note.
 */
function BdMineRoute() {
  const user = useAuth((s) => s.user);
  const ready = useAuth((s) => s.ready);
  if (!ready) return <FullPageLoader />;
  if (!user) return <Redirect to={`/signin?next=${encodeURIComponent('/bd')}`} />;
  return <BusinessDevelopmentMinePage />;
}

/**
 * Wraps a route with whichever shell matches the current auth state.
 * Used for footer-accessible pages (legal, about, contact) that need to
 * render correctly whether the user is signed in or not.
 */
function ShellRoute({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const ready = useAuth((s) => s.ready);
  if (!ready) return <FullPageLoader />;
  if (user) return <AppShell>{children}</AppShell>;
  return <PublicShell>{children}</PublicShell>;
}


